import React from 'react';
import { t } from '../../services/locale';
import type { ProfileAccountBranch, ProfileAccountFormatters, ProfileTransaction } from './profileAccount.types';
import {
  getTransactionDate,
  getTransactionName,
  getTransactionPaymentMethod,
  getTransactionStatusTag,
  getTransactionType,
  getTransactionTypeTagClass,
} from './profileTransactionRules';

export type ProfileTransactionsListData = {
  transactions?: ProfileTransaction[];
  totalTransactions?: number;
  moreTransactions?: boolean;
  branch?: ProfileAccountBranch | null;
  formatUnix?: ProfileAccountFormatters['formatUnix'];
  /** Host `isoCurrency` filter so amounts match Angular output. */
  formatMoney?: (amount: number, currency: string) => string;
  onShowMore?: () => void;
};

type Props = Readonly<{
  data?: ProfileTransactionsListData;
}>;

const emptyFormatUnix: ProfileAccountFormatters['formatUnix'] = () => '';

function formatAmount(
  transaction: ProfileTransaction,
  formatMoney?: ProfileTransactionsListData['formatMoney']
): string {
  if (transaction.amount == null) {
    return '';
  }
  if (formatMoney) {
    return formatMoney(transaction.amount, transaction.currency ?? '');
  }
  return String(transaction.amount);
}

function TransactionStatus({
  transaction,
  index,
}: Readonly<{ transaction: ProfileTransaction; index: number }>) {
  const tag = getTransactionStatusTag(transaction);
  if (tag === 'pending') {
    return (
      <div id={`profile-transaction-${index}-status-pending`} className="blue-outline-white-tag">
        {t('WEB_PENDING')}
      </div>
    );
  }
  if (tag === 'failed') {
    return (
      <div id={`profile-transaction-${index}-status-failed`} className="red-tag">
        {t('WEB_FAILED')}
      </div>
    );
  }
  return null;
}

function TransactionRow({
  transaction,
  index,
  data,
  formatUnix,
}: Readonly<{
  transaction: ProfileTransaction;
  index: number;
  data: ProfileTransactionsListData;
  formatUnix: ProfileAccountFormatters['formatUnix'];
}>) {
  return (
    <li id={`profile-transaction-${index}`}>
      <div className="list-text-left">
        <h5 id={`profile-transaction-${index}-name`}>{getTransactionName(transaction)}</h5>
        <p id={`profile-transaction-${index}-date`}>{getTransactionDate(transaction, data.branch, formatUnix)}</p>
        <TransactionStatus transaction={transaction} index={index} />
        <div
          id={`profile-transaction-${index}-type`}
          className={getTransactionTypeTagClass(transaction)}
        >
          {getTransactionType(transaction)}
        </div>
      </div>
      <div className="list-text-right">
        <div id={`profile-transaction-${index}-price`} className="price">
          {formatAmount(transaction, data.formatMoney)}
        </div>
        <div id={`profile-transaction-${index}-payment-method`} className="type">
          {getTransactionPaymentMethod(transaction)}
        </div>
      </div>
    </li>
  );
}

export function ProfileTransactionsList({ data }: Props) {
  if (!data) {
    return null;
  }

  const formatUnix = data.formatUnix ?? emptyFormatUnix;
  const transactions = data.transactions ?? [];
  const totalTransactions = data.totalTransactions ?? 0;

  if (transactions.length === 0) {
    return (
      <>
        <div id="profile-transactions-count" className="list-label">
          {totalTransactions} {t('WEB_TRANSACTIONS')}
        </div>
        <ul className="list">
          <li>
            <a id="profile-transactions-empty-message" className="show-more-contact">
              {t('WEB_THERE_ARE_NO_TRANSACTIONS_YET')}
            </a>
          </li>
        </ul>
      </>
    );
  }

  return (
    <>
      <div id="profile-transactions-count" className="list-label">
        {totalTransactions} {t('WEB_TRANSACTIONS')}
      </div>
      <ul className="list">
        {transactions.map((transaction, index) => (
          <TransactionRow
            key={transaction._id ?? index}
            transaction={transaction}
            index={index}
            data={data}
            formatUnix={formatUnix}
          />
        ))}
        {data.moreTransactions ? (
          <li>
            <a
              id="profile-transactions-show-more"
              className="show-more"
              href="#"
              onClick={(event) => {
                event.preventDefault();
                data.onShowMore?.();
              }}
            >
              {t('WEB_SHOW_MORE')}
            </a>
          </li>
        ) : null}
      </ul>
    </>
  );
}
